(() => {
  'use strict';

  const SELECTOR = '.jol-card.jol-card--parchment .jol-card__image';
  const TARGET_FILL = 0.875;
  const MIN_SCALE = 0.78;
  const MAX_SCALE = 1.58;
  const SAMPLE_MAX = 256;
  const ALPHA_CUTOFF = 18;
  const MIN_TRANSPARENT_FRACTION = 0.025;
  const cache = new Map();

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  function sourceFor(img) {
    return img.currentSrc || img.src || '';
  }

  function manualFactor(img) {
    const value = parseFloat(getComputedStyle(img).getPropertyValue('--jol-global-card-manual-factor'));
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  function clearAuto(img) {
    img.style.removeProperty('--jol-auto-scale');
    img.style.removeProperty('--jol-auto-shift-x');
    img.style.removeProperty('--jol-auto-shift-y');
  }

  async function measure(url) {
    if (cache.has(url)) return cache.get(url);
    const promise = new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => {
        try {
          const sw = probe.naturalWidth || probe.width;
          const sh = probe.naturalHeight || probe.height;
          if (!sw || !sh) return resolve(null);
          const ratio = Math.min(1, SAMPLE_MAX / Math.max(sw, sh));
          const w = Math.max(1, Math.round(sw * ratio));
          const h = Math.max(1, Math.round(sh * ratio));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.clearRect(0, 0, w, h);
          ctx.drawImage(probe, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h).data;
          let minX = w, minY = h, maxX = -1, maxY = -1, opaque = 0;
          const total = w * h;
          for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
              const a = data[(y * w + x) * 4 + 3];
              if (a > ALPHA_CUTOFF) {
                opaque++;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (!opaque || maxX < minX || maxY < minY) return resolve(null);
          const transparentFraction = 1 - opaque / total;
          if (transparentFraction < MIN_TRANSPARENT_FRACTION) {
            return resolve({ opaque: true });
          }
          resolve({
            opaque: false,
            sourceWidth: w,
            sourceHeight: h,
            left: minX,
            top: minY,
            right: maxX + 1,
            bottom: maxY + 1
          });
        } catch (e) {
          resolve(null);
        }
      };
      probe.onerror = () => resolve(null);
      probe.src = url;
    });
    cache.set(url, promise);
    return promise;
  }

  async function normalize(img) {
    if (!(img instanceof HTMLImageElement)) return;
    const url = sourceFor(img);
    if (!url) return;
    const token = url;
    img.dataset.jolAutoSizingToken = token;

    const bounds = await measure(url);
    if (img.dataset.jolAutoSizingToken !== token) return;
    if (!bounds || bounds.opaque) {
      clearAuto(img);
      return;
    }

    const media = img.closest('.jol-card__media');
    if (!media) return;
    const mw = media.clientWidth;
    const mh = media.clientHeight;
    if (!mw || !mh) {
      requestAnimationFrame(() => normalize(img));
      return;
    }

    const sw = bounds.sourceWidth;
    const sh = bounds.sourceHeight;
    const sourceAspect = sw / sh;
    const mediaAspect = mw / mh;
    let rw, rh;
    if (sourceAspect >= mediaAspect) {
      rw = mw;
      rh = mw / sourceAspect;
    } else {
      rh = mh;
      rw = mh * sourceAspect;
    }

    const bw = bounds.right - bounds.left;
    const bh = bounds.bottom - bounds.top;
    const visibleW = rw * (bw / sw);
    const visibleH = rh * (bh / sh);
    if (!visibleW || !visibleH) return;

    let autoScale = Math.min((mw * TARGET_FILL) / visibleW, (mh * TARGET_FILL) / visibleH);
    autoScale = clamp(autoScale, MIN_SCALE, MAX_SCALE);

    const cx = (bounds.left + bounds.right) / 2;
    const cy = (bounds.top + bounds.bottom) / 2;
    const offsetX = (cx / sw - 0.5) * rw;
    const offsetY = (cy / sh - 0.5) * rh;
    const combinedScale = autoScale * manualFactor(img);
    const shiftX = clamp(-offsetX * combinedScale, -mw * 0.22, mw * 0.22);
    const shiftY = clamp(-offsetY * combinedScale, -mh * 0.22, mh * 0.22);

    img.style.setProperty('--jol-auto-scale', autoScale.toFixed(4));
    img.style.setProperty('--jol-auto-shift-x', `${shiftX.toFixed(2)}px`);
    img.style.setProperty('--jol-auto-shift-y', `${shiftY.toFixed(2)}px`);
    img.dataset.jolAutoSized = 'true';
  }

  function scan(root = document) {
    const images = root.matches?.(SELECTOR) ? [root] : root.querySelectorAll?.(SELECTOR) || [];
    images.forEach((img) => {
      if (img.complete && img.naturalWidth) normalize(img);
      else img.addEventListener('load', () => normalize(img), { once: true });
    });
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => document.querySelectorAll(SELECTOR).forEach(normalize), 140);
  }, { passive: true });

  window.addEventListener('jol:global-image-scales', () => {
    document.querySelectorAll(SELECTOR).forEach(normalize);
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) scan(node);
      });
      if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) {
        normalize(mutation.target);
      }
    }
  });

  function init() {
    scan(document);
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
